import api from './client';

/**
 * Thin wrappers around the API. One module per resource keeps feature code
 * declarative — mirror this file when you add a new resource.
 */
export const AdminsApi = {
  list: (params) => api.get('/admins', { params }).then((r) => r.data),
  get: (id) => api.get(`/admins/${id}`).then((r) => r.data.data),
  create: (payload) => api.post('/admins', payload).then((r) => r.data.data),
  update: (id, payload) => api.put(`/admins/${id}`, payload).then((r) => r.data.data),
  remove: (id) => api.delete(`/admins/${id}`),
  syncPermissions: (id, permissions) =>
    api.put(`/admins/${id}/permissions`, { permissions }).then((r) => r.data.data),
};

export const PermissionsApi = {
  // -> [{ group, permissions: [name, ...] }]
  catalogue: () => api.get('/permissions').then((r) => r.data.data),
};

export const CrmApi = {
  lookups: () => api.get('/crm/lookups').then((r) => r.data.data),

  leads: {
    list: (params) => api.get('/crm/leads', { params }).then((r) => r.data),
    get: (id) => api.get(`/crm/leads/${id}`).then((r) => r.data.data),
    create: (payload) => api.post('/crm/leads', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/leads/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/leads/${id}`),
  },

  followups: {
    create: (leadId, payload) =>
      api.post(`/crm/leads/${leadId}/followups`, payload).then((r) => r.data.data),
  },

  statuses: {
    list: () => api.get('/crm/statuses').then((r) => r.data.data),
    create: (payload) => api.post('/crm/statuses', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/statuses/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/statuses/${id}`),
  },

  customFields: {
    // entity: 'lead' | 'followup' (omit for all)
    list: (entity) =>
      api.get('/crm/custom-fields', { params: { entity } }).then((r) => r.data.data),
    create: (payload) => api.post('/crm/custom-fields', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/custom-fields/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/custom-fields/${id}`),
  },
};

export const WhatsAppApi = {
  // chats
  chats: (params) => api.get('/whatsapp/chats', { params }).then((r) => r.data.data),
  unreadCount: () => api.get('/whatsapp/chats/unread-count').then((r) => r.data.data.count),
  messages: (chatId, afterId) =>
    api
      .get(`/whatsapp/chats/${chatId}/messages`, { params: afterId ? { after_id: afterId } : {} })
      .then((r) => r.data.data),
  // payload: plain object; files/arrays are sent as multipart
  send: (chatId, payload) => api.post(`/whatsapp/chats/${chatId}/messages`, toForm(payload)).then((r) => r.data.data),
  initiate: (payload) => api.post('/whatsapp/chats/initiate', toForm(payload)).then((r) => r.data.data),
  assign: (chatId, userId) =>
    api.post(`/whatsapp/chats/${chatId}/assign`, { user_id: userId }).then((r) => r.data.data),
  unassign: (chatId) => api.post(`/whatsapp/chats/${chatId}/unassign`).then((r) => r.data.data),
  toggleBot: (chatId, paused) =>
    api.post(`/whatsapp/chats/${chatId}/bot`, { paused }).then((r) => r.data.data),
  staffOptions: () => api.get('/whatsapp/chats/staff-options').then((r) => r.data.data),
  mediaBlob: (chatId, messageId) =>
    api
      .get(`/whatsapp/chats/${chatId}/messages/${messageId}/media`, { responseType: 'blob' })
      .then((r) => r.data),

  // templates
  templates: (params) => api.get('/whatsapp/templates', { params }).then((r) => r.data.data),
  createTemplate: (payload) => api.post('/whatsapp/templates', toForm(payload)).then((r) => r.data.data),
  syncTemplates: () => api.post('/whatsapp/templates/sync').then((r) => r.data.data),
  deleteTemplate: (id) => api.delete(`/whatsapp/templates/${id}`),
  templateImageBlob: (id) =>
    api.get(`/whatsapp/templates/${id}/header-image`, { responseType: 'blob' }).then((r) => r.data),

  // bot
  bot: () => api.get('/whatsapp/bot').then((r) => r.data.data),
  botStaff: () => api.get('/whatsapp/bot/staff-options').then((r) => r.data.data),
  saveBotSettings: (payload) => api.put('/whatsapp/bot/settings', payload).then((r) => r.data.data),
  saveRule: (id, payload) =>
    (id ? api.put(`/whatsapp/bot/rules/${id}`, payload) : api.post('/whatsapp/bot/rules', payload)).then(
      (r) => r.data.data
    ),
  deleteRule: (id) => api.delete(`/whatsapp/bot/rules/${id}`),
  uploadRuleMedia: (id, file) =>
    api.post(`/whatsapp/bot/rules/${id}/media`, toForm({ file })).then((r) => r.data.data),
  deleteRuleMedia: (id) => api.delete(`/whatsapp/bot/rules/${id}/media`).then((r) => r.data.data),
  simulate: (payload) => api.post('/whatsapp/bot/simulate', payload).then((r) => r.data.data),
};

/** Object -> FormData. Arrays become key[]; null/undefined/'' are skipped. */
function toForm(obj) {
  const fd = new FormData();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (Array.isArray(v)) v.forEach((item) => fd.append(`${k}[]`, item ?? ''));
    else fd.append(k, v);
  });
  return fd;
}

export const CallsApi = {
  list: (params) => api.get('/crm/calls', { params }).then((r) => r.data),
  summary: (params) => api.get('/crm/calls/summary', { params }).then((r) => r.data.data),
  remove: (id) => api.delete(`/crm/calls/${id}`),
  recordingBlob: (id) =>
    api.get(`/crm/call-recordings/${id}/stream`, { responseType: 'blob' }).then((r) => r.data),
  leadCalls: (leadId) => api.get(`/crm/leads/${leadId}/calls`).then((r) => r.data.data),
  linkableCalls: (leadId) => api.get(`/crm/leads/${leadId}/linkable-calls`).then((r) => r.data.data),
};

export const DevicesApi = {
  list: () => api.get('/crm/devices').then((r) => r.data.data),
  forceLogout: (id) => api.post(`/crm/devices/${id}/force-logout`),
  remove: (id) => api.delete(`/crm/devices/${id}`),
};
